import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, CheckCircle, ArrowLeft, Ticket, Music, Upload, Image } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';

const API = "/api";

const SubmitEvent = () => {
  const { user, token } = useAuth();
  const [submitted, setSubmitted] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [form, setForm] = useState({
    submitter_type: '',
    submitter_name: '',
    submitter_email: '',
    event_title: '',
    venue_name: '',
    venue_address: '',
    date: '',
    time: '',
    description: '',
    performing_artists: '',
    headliner: '',
    ticket_price: '',
    ticket_url: '',
    is_free: false,
    is_all_ages: false,
    event_image: '',
    promo_video: ''
  });

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Image = reader.result;
        setImagePreview(base64Image);
        updateForm('event_image', base64Image);
        setUploadingImage(false);
        toast.success('Image uploaded!');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Failed to upload image:', error);
      toast.error('Failed to upload image');
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/events/submit`, form);
      setSubmissionId(response.data.id);
      setSubmitted(true);
      toast.success('Event submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit event');
    } finally {
      setLoading(false);
    }
  };

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="event-submission-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md space-y-6"
        >
          <div className="w-20 h-20 mx-auto rounded-full bg-secondary/20 flex items-center justify-center">
            <CheckCircle size={40} className="text-secondary" />
          </div>
          <h1 className="font-heading text-3xl font-bold">Event Submitted!</h1>
          <p className="text-muted-foreground">
            Thank you for submitting your event to Twin Tube! We'll review it and get it listed within 24-48 hours.
          </p>
          <div className="p-4 bg-card rounded-sm border border-border">
            <p className="text-sm text-muted-foreground mb-1">Your Reference ID:</p>
            <code className="text-primary font-mono text-sm">{submissionId}</code>
          </div>
          <div className="flex gap-3 justify-center">
            <Link to="/">
              <Button variant="outline">Back to Home</Button>
            </Link>
            <Link to="/events">
              <Button>View Events</Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="submit-event-page">
      {/* Back Link */}
      <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft size={16} />
        Back to Home
      </Link>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-4"
      >
        <div className="w-16 h-16 mx-auto rounded-full bg-primary/20 flex items-center justify-center">
          <Calendar size={32} className="text-primary" />
        </div>
        
        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
          Submit an Event
        </h1>
        <p className="text-muted-foreground max-w-xl mx-auto">
          Get your show listed on Twin Tube and reach music fans across the Twin Cities.
          Open to bands, venues, and promoters.
        </p>
      </motion.div>

      {/* Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Submitter Info */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading">Your Information</CardTitle>
              <CardDescription>Who is submitting this event?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>I am a... *</Label>
                <Select value={form.submitter_type} onValueChange={(v) => updateForm('submitter_type', v)} required>
                  <SelectTrigger data-testid="submitter-type-select">
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="band">Band / Artist</SelectItem>
                    <SelectItem value="venue">Venue</SelectItem>
                    <SelectItem value="promoter">Promoter</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Your Name / Organization *</Label>
                  <Input
                    value={form.submitter_name}
                    onChange={(e) => updateForm('submitter_name', e.target.value)}
                    placeholder="Band name, venue name, or your name"
                    required
                    data-testid="submitter-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={form.submitter_email}
                    onChange={(e) => updateForm('submitter_email', e.target.value)}
                    placeholder="your@email.com"
                    required
                    data-testid="submitter-email-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Event Details */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading">Event Details</CardTitle>
              <CardDescription>Tell us about the show</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Event Title *</Label>
                <Input
                  value={form.event_title}
                  onChange={(e) => updateForm('event_title', e.target.value)}
                  placeholder="e.g., 'Summer Jam 2025' or 'Local Showcase Night'"
                  required
                  data-testid="event-title-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Venue Name *</Label>
                  <Input
                    value={form.venue_name}
                    onChange={(e) => updateForm('venue_name', e.target.value)}
                    placeholder="e.g., First Avenue"
                    required
                    data-testid="venue-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Venue Address</Label>
                  <Input
                    value={form.venue_address}
                    onChange={(e) => updateForm('venue_address', e.target.value)}
                    placeholder="e.g., 701 1st Ave N, Minneapolis"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={form.date}
                    onChange={(e) => updateForm('date', e.target.value)}
                    required
                    data-testid="date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Time *</Label>
                  <Input
                    value={form.time}
                    onChange={(e) => updateForm('time', e.target.value)}
                    placeholder="e.g., 8:00 PM"
                    required
                    data-testid="time-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => updateForm('description', e.target.value)}
                  placeholder="Tell people what to expect at this show..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Artists */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Music size={20} /> Performing Artists
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Headliner</Label>
                <Input
                  value={form.headliner}
                  onChange={(e) => updateForm('headliner', e.target.value)}
                  placeholder="Main act"
                />
              </div>
              <div className="space-y-2">
                <Label>All Performing Artists</Label>
                <Input
                  value={form.performing_artists}
                  onChange={(e) => updateForm('performing_artists', e.target.value)}
                  placeholder="e.g., Band 1, Band 2, DJ Name"
                />
                <p className="text-xs text-muted-foreground">Separate multiple artists with commas</p>
              </div>
            </CardContent>
          </Card>

          {/* Tickets */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Ticket size={20} /> Tickets & Access
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-muted rounded-sm">
                <div>
                  <p className="font-medium">Free Event</p>
                  <p className="text-xs text-muted-foreground">No tickets required</p>
                </div>
                <Switch
                  checked={form.is_free}
                  onCheckedChange={(v) => updateForm('is_free', v)}
                  data-testid="is-free-switch"
                />
              </div>

              {!form.is_free && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Ticket Price</Label>
                    <Input
                      value={form.ticket_price}
                      onChange={(e) => updateForm('ticket_price', e.target.value)}
                      placeholder="e.g., $15 / $20 at door"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ticket URL</Label>
                    <Input
                      value={form.ticket_url}
                      onChange={(e) => updateForm('ticket_url', e.target.value)}
                      placeholder="https://..."
                    />
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between p-3 bg-muted rounded-sm">
                <div>
                  <p className="font-medium">All Ages</p>
                  <p className="text-xs text-muted-foreground">Open to all ages</p>
                </div>
                <Switch
                  checked={form.is_all_ages}
                  onCheckedChange={(v) => updateForm('is_all_ages', v)}
                  data-testid="is-all-ages-switch"
                />
              </div>
            </CardContent>
          </Card>

          {/* Media */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading">Event Media</CardTitle>
              <CardDescription>Add images or video to promote your event</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Event Image</Label>
                
                {/* Image Preview */}
                {(imagePreview || form.event_image) && (
                  <div className="relative w-full h-48 rounded-lg overflow-hidden bg-muted mb-3">
                    <img
                      src={imagePreview || form.event_image}
                      alt="Event preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                {/* Upload Button */}
                <div className="flex gap-2">
                  <label className="flex-1">
                    <div className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed border-muted-foreground/25 rounded-lg cursor-pointer hover:border-purple-500/50 transition-colors ${uploadingImage ? 'opacity-50' : ''}`}>
                      <Upload size={20} className="text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {uploadingImage ? 'Uploading...' : 'Upload Image'}
                      </span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploadingImage}
                    />
                  </label>
                </div>
                
                {/* Or URL Input */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-muted" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">or paste URL</span>
                  </div>
                </div>
                
                <Input
                  value={form.event_image.startsWith('data:') ? '' : form.event_image}
                  onChange={(e) => {
                    updateForm('event_image', e.target.value);
                    setImagePreview(null);
                  }}
                  placeholder="https://... (poster or flyer image)"
                />
              </div>
              <div className="space-y-2">
                <Label>Promo Video URL</Label>
                <Input
                  value={form.promo_video}
                  onChange={(e) => updateForm('promo_video', e.target.value)}
                  placeholder="https://youtube.com/... (optional)"
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <Button 
            type="submit" 
            size="lg" 
            className="w-full gap-2"
            disabled={loading}
            data-testid="submit-btn"
          >
            {loading ? 'Submitting...' : 'Submit Event'}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            Events are typically reviewed and published within 24-48 hours.
            Make sure all information is accurate before submitting.
          </p>
        </form>
      </motion.div>
    </div>
  );
};

export default SubmitEvent;
